import React, { useState, useCallback, useRef, useEffect } from 'react';
import { Upload, Download, AlertCircle, RotateCw, RotateCcw, FlipHorizontal, FlipVertical, Compass, RotateCcw as ResetIcon } from 'lucide-react';

type OutputFormat = 'png' | 'jpg';

interface TransformState {
  rotation: number;
  freeRotation: number;
  flipH: boolean;
  flipV: boolean;
}

const DEFAULT_TRANSFORM: TransformState = {
  rotation: 0,
  freeRotation: 0,
  flipH: false,
  flipV: false,
};

const RotateFlipImage: React.FC = () => {
  const [file, setFile] = useState<File | null>(null);
  const [originalPreview, setOriginalPreview] = useState<string | null>(null);
  const [transform, setTransform] = useState<TransformState>({ ...DEFAULT_TRANSFORM });
  const [bgColor, setBgColor] = useState<'transparent' | 'white'>('transparent');
  const [outputFormat, setOutputFormat] = useState<OutputFormat>('png');
  const [jpgQuality, setJpgQuality] = useState(92);
  const [error, setError] = useState<string | null>(null);
  const [imgElement, setImgElement] = useState<HTMLImageElement | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const previewCanvasRef = useRef<HTMLCanvasElement>(null);

  const totalRotation = transform.rotation + transform.freeRotation;

  const handleFileSelect = useCallback((selectedFiles: FileList | null) => {
    if (!selectedFiles || selectedFiles.length === 0) return;
    const f = selectedFiles[0];
    if (!f.type.startsWith('image/')) {
      setError('Please select a valid image file.');
      return;
    }
    setFile(f);
    setError(null);
    setTransform({ ...DEFAULT_TRANSFORM });
    const reader = new FileReader();
    reader.onload = (e) => {
      const url = e.target?.result as string;
      setOriginalPreview(url);
      const img = new window.Image();
      img.onload = () => setImgElement(img);
      img.onerror = () => setError('Failed to load image.');
      img.src = url;
    };
    reader.readAsDataURL(f);
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    handleFileSelect(e.dataTransfer.files);
  }, [handleFileSelect]);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
  }, []);

  const drawTransformed = useCallback((canvas: HTMLCanvasElement, img: HTMLImageElement) => {
    const radians = (totalRotation * Math.PI) / 180;
    const cos = Math.abs(Math.cos(radians));
    const sin = Math.abs(Math.sin(radians));
    const w = Math.ceil(img.naturalWidth * cos + img.naturalHeight * sin);
    const h = Math.ceil(img.naturalWidth * sin + img.naturalHeight * cos);

    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    // Background
    if (bgColor === 'white') {
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, w, h);
    } else {
      ctx.clearRect(0, 0, w, h);
    }

    ctx.save();
    ctx.translate(w / 2, h / 2);
    ctx.rotate(radians);
    ctx.scale(transform.flipH ? -1 : 1, transform.flipV ? -1 : 1);
    ctx.drawImage(img, -img.naturalWidth / 2, -img.naturalHeight / 2);
    ctx.restore();
  }, [totalRotation, transform.flipH, transform.flipV, bgColor]);

  // Live preview
  useEffect(() => {
    if (!imgElement || !previewCanvasRef.current) return;
    drawTransformed(previewCanvasRef.current, imgElement);
  }, [imgElement, drawTransformed]);

  const rotateCW = useCallback(() => {
    setTransform((prev) => ({ ...prev, rotation: (prev.rotation + 90) % 360 }));
  }, []);

  const rotateCCW = useCallback(() => {
    setTransform((prev) => ({ ...prev, rotation: (prev.rotation + 270) % 360 }));
  }, []);

  const rotate180 = useCallback(() => {
    setTransform((prev) => ({ ...prev, rotation: (prev.rotation + 180) % 360 }));
  }, []);

  const flipH = useCallback(() => {
    setTransform((prev) => ({ ...prev, flipH: !prev.flipH }));
  }, []);

  const flipV = useCallback(() => {
    setTransform((prev) => ({ ...prev, flipV: !prev.flipV }));
  }, []);

  const autoStraighten = useCallback(() => {
    setTransform((prev) => ({ ...prev, freeRotation: 0 }));
  }, []);

  const resetTransform = useCallback(() => {
    setTransform({ ...DEFAULT_TRANSFORM });
  }, []);

  const getStateDescription = useCallback(() => {
    const parts: string[] = [];
    if (transform.rotation !== 0) {
      parts.push(`Rotated ${transform.rotation} deg`);
    }
    if (transform.freeRotation !== 0) {
      parts.push(`Free ${transform.freeRotation > 0 ? '+' : ''}${transform.freeRotation} deg`);
    }
    if (transform.flipH) parts.push('Flipped H');
    if (transform.flipV) parts.push('Flipped V');
    return parts.length > 0 ? parts.join(', ') : 'No changes';
  }, [transform]);

  const downloadResult = useCallback(() => {
    if (!imgElement || !file) return;
    const canvas = canvasRef.current;
    if (!canvas) return;
    drawTransformed(canvas, imgElement);

    const mimeType = outputFormat === 'jpg' ? 'image/jpeg' : 'image/png';
    const quality = outputFormat === 'jpg' ? jpgQuality / 100 : undefined;

    canvas.toBlob((blob) => {
      if (!blob) return;
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      const name = file.name.replace(/\.[^.]+$/, '');
      const ext = outputFormat === 'jpg' ? 'jpg' : 'png';
      link.download = `${name}_transformed.${ext}`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(link.href);
    }, mimeType, quality);
  }, [imgElement, file, drawTransformed, outputFormat, jpgQuality]);

  const reset = useCallback(() => {
    setFile(null);
    setOriginalPreview(null);
    setImgElement(null);
    setTransform({ ...DEFAULT_TRANSFORM });
    setBgColor('transparent');
    setOutputFormat('png');
    setJpgQuality(92);
    setError(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  }, []);

  const fixedButtons = [
    { label: '90 CW', icon: RotateCw, action: rotateCW },
    { label: '90 CCW', icon: RotateCcw, action: rotateCCW },
    { label: '180', icon: RotateCw, action: rotate180 },
  ];

  const flipButtons = [
    { label: 'Flip H', icon: FlipHorizontal, action: flipH, active: transform.flipH },
    { label: 'Flip V', icon: FlipVertical, action: flipV, active: transform.flipV },
  ];

  return (
    <div className="space-y-6">
      <canvas ref={canvasRef} className="hidden" />

      {/* Header */}
      <div className="text-center">
        <div className="flex justify-center mb-4">
          <div className="p-3 bg-indigo-100 rounded-full">
            <RotateCw className="w-8 h-8 text-indigo-600" />
          </div>
        </div>
        <h2 className="text-2xl font-bold text-gray-900 mb-2">Rotate & Flip Image</h2>
        <p className="text-gray-600">
          Fixed rotations, free rotation, flip, and auto-straighten with live preview.
        </p>
      </div>

      {/* Upload */}
      {!file && (
        <div
          onDrop={handleDrop}
          onDragOver={handleDragOver}
          className="border-2 border-dashed border-gray-300 rounded-xl p-8 text-center hover:border-indigo-400 transition-colors cursor-pointer"
          onClick={() => fileInputRef.current?.click()}
        >
          <Upload className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            Drop an image here or click to select
          </h3>
          <p className="text-gray-600">Supports PNG, JPG, WebP and more</p>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            onChange={(e) => handleFileSelect(e.target.files)}
            className="hidden"
          />
        </div>
      )}

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start">
          <AlertCircle className="w-5 h-5 text-red-500 mr-2 mt-0.5 flex-shrink-0" />
          <span className="text-red-700">{error}</span>
        </div>
      )}

      {file && imgElement && (
        <div className="space-y-4">
          {/* Current State */}
          <div className="bg-indigo-50 border border-indigo-200 rounded-xl p-3 text-sm text-indigo-800 font-medium text-center">
            {getStateDescription()}
          </div>

          {/* Fixed Rotation Buttons */}
          <div className="bg-gray-50 rounded-xl p-4">
            <label className="block text-sm font-medium text-gray-700 mb-3">Fixed Rotations</label>
            <div className="grid grid-cols-3 gap-2">
              {fixedButtons.map((btn) => (
                <button
                  key={btn.label}
                  onClick={btn.action}
                  className="flex items-center justify-center gap-2 px-4 py-3 bg-white border border-gray-200 rounded-lg text-sm font-medium text-gray-700 hover:border-indigo-300 hover:text-indigo-600 transition-colors"
                >
                  <btn.icon className="w-4 h-4" />
                  {btn.label}
                </button>
              ))}
            </div>
          </div>

          {/* Flip Buttons */}
          <div className="bg-gray-50 rounded-xl p-4">
            <label className="block text-sm font-medium text-gray-700 mb-3">Flip</label>
            <div className="grid grid-cols-2 gap-2">
              {flipButtons.map((btn) => (
                <button
                  key={btn.label}
                  onClick={btn.action}
                  className={`flex items-center justify-center gap-2 px-4 py-3 rounded-lg text-sm font-medium transition-colors border-2 ${
                    btn.active
                      ? 'border-indigo-500 bg-indigo-50 text-indigo-700'
                      : 'border-gray-200 bg-white text-gray-700 hover:border-indigo-300'
                  }`}
                >
                  <btn.icon className="w-4 h-4" />
                  {btn.label}
                </button>
              ))}
            </div>
          </div>

          {/* Free Rotation */}
          <div className="bg-gray-50 rounded-xl p-4">
            <div className="flex justify-between items-center mb-3">
              <label className="text-sm font-medium text-gray-700">Free Rotation</label>
              <div className="flex items-center gap-2">
                <input
                  type="number"
                  min={-180}
                  max={180}
                  value={transform.freeRotation}
                  onChange={(e) => {
                    const val = Math.max(-180, Math.min(180, parseInt(e.target.value) || 0));
                    setTransform((prev) => ({ ...prev, freeRotation: val }));
                  }}
                  className="w-20 px-2 py-1 text-sm text-right border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                />
                <span className="text-sm font-bold text-indigo-600">deg</span>
              </div>
            </div>
            <input
              type="range"
              min={-180}
              max={180}
              value={transform.freeRotation}
              onChange={(e) => setTransform((prev) => ({ ...prev, freeRotation: parseInt(e.target.value) }))}
              className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-indigo-600"
            />
            <div className="flex justify-between text-xs text-gray-500 mt-1">
              <span>-180 deg</span>
              <span>0 deg</span>
              <span>180 deg</span>
            </div>
          </div>

          {/* Auto-straighten and Background */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <button
              onClick={autoStraighten}
              className="flex items-center justify-center gap-2 px-4 py-3 bg-white border border-gray-200 rounded-xl text-sm font-medium text-gray-700 hover:border-indigo-300 hover:text-indigo-600 transition-colors"
            >
              <Compass className="w-4 h-4" />
              Auto-Straighten
            </button>
            <div className="flex items-center gap-2 px-4 py-3 bg-white border border-gray-200 rounded-xl">
              <label className="text-sm font-medium text-gray-700 mr-auto">Background</label>
              <button
                onClick={() => setBgColor('transparent')}
                className={`px-3 py-1 rounded text-xs font-medium transition-colors ${
                  bgColor === 'transparent'
                    ? 'bg-indigo-600 text-white'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                Transparent
              </button>
              <button
                onClick={() => setBgColor('white')}
                className={`px-3 py-1 rounded text-xs font-medium transition-colors ${
                  bgColor === 'white'
                    ? 'bg-indigo-600 text-white'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                White
              </button>
            </div>
          </div>

          {/* Output Format */}
          <div className="bg-gray-50 rounded-xl p-4">
            <label className="block text-sm font-medium text-gray-700 mb-3">Output Format</label>
            <div className="flex flex-wrap items-end gap-4">
              <div className="flex gap-2">
                {(['png', 'jpg'] as OutputFormat[]).map((fmt) => (
                  <button
                    key={fmt}
                    onClick={() => setOutputFormat(fmt)}
                    className={`px-4 py-2 rounded-lg text-sm font-medium uppercase transition-colors ${
                      outputFormat === fmt
                        ? 'bg-indigo-600 text-white'
                        : 'bg-white border border-gray-200 text-gray-700 hover:border-indigo-300'
                    }`}
                  >
                    {fmt}
                  </button>
                ))}
              </div>
              {outputFormat === 'jpg' && (
                <div className="flex-1 min-w-[200px]">
                  <div className="flex justify-between items-center mb-1">
                    <label className="text-xs font-medium text-gray-600">JPG Quality</label>
                    <span className="text-xs font-bold text-gray-900">{jpgQuality}%</span>
                  </div>
                  <input
                    type="range"
                    min={10}
                    max={100}
                    value={jpgQuality}
                    onChange={(e) => setJpgQuality(parseInt(e.target.value))}
                    className="w-full h-1.5 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-indigo-600"
                  />
                </div>
              )}
            </div>
          </div>

          {/* Previews */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <h3 className="text-sm font-medium text-gray-700 mb-2">Original</h3>
              <div className="bg-gray-100 rounded-xl overflow-hidden flex items-center justify-center min-h-[200px]">
                {originalPreview && (
                  <img src={originalPreview} alt="Original" className="max-w-full max-h-[400px] object-contain" />
                )}
              </div>
            </div>
            <div>
              <h3 className="text-sm font-medium text-gray-700 mb-2">Transformed Preview</h3>
              <div className="bg-gray-100 rounded-xl overflow-hidden flex items-center justify-center min-h-[200px]">
                <canvas ref={previewCanvasRef} className="max-w-full max-h-[400px] object-contain" />
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex flex-wrap gap-3">
            <button
              onClick={downloadResult}
              className="flex-1 flex items-center justify-center gap-2 px-6 py-3 bg-indigo-600 text-white font-medium rounded-xl hover:bg-indigo-700 transition-colors"
            >
              <Download className="w-5 h-5" />
              Download
            </button>
            <button
              onClick={resetTransform}
              className="flex items-center justify-center gap-2 px-4 py-3 bg-gray-200 text-gray-700 font-medium rounded-xl hover:bg-gray-300 transition-colors"
              title="Reset transform"
            >
              <ResetIcon className="w-5 h-5" />
              Reset Transform
            </button>
            <button
              onClick={reset}
              className="flex items-center justify-center gap-2 px-4 py-3 bg-gray-200 text-gray-700 font-medium rounded-xl hover:bg-gray-300 transition-colors"
              title="Start over"
            >
              <Upload className="w-5 h-5" />
              New Image
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default RotateFlipImage;
